/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, ErrorText, Textarea, Pressable } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { Footer } from '@components/systems'
import { Select, SelectChangeEvent, MenuItem, InputLabel } from '@mui/material'

import {
	useAppDispatch,
	useAppSelector,
	inquiryActions,
	InquiryStateType,
} from '@redux/index'
import { produce } from 'immer'
import { grey, deepPurple } from '@mui/material/colors'

const ContactPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const inquiry: InquiryStateType['insertInquiry'] = useAppSelector(
		state => state.inquiry.insertInquiry,
	)
	const errorMessages: InquiryStateType['inquiryErrorMessage'] = useAppSelector(
		state => state.inquiry.inquiryErrorMessage,
	)
	const showModal: boolean = useAppSelector(state => state.inquiry.showModal)
	const modalInfo: ModalInfoType = useAppSelector(state => state.inquiry.modalInfo)

	const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newInquiry = produce(inquiry, draft => {
			draft.name = str
		})

		reduxDispatch(inquiryActions.INSERT_INQUIRY(newInquiry))
	}

	const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newInquiry = produce(inquiry, draft => {
			draft.email = str
		})

		reduxDispatch(inquiryActions.INSERT_INQUIRY(newInquiry))
	}

	const onCategChange = (e: SelectChangeEvent) => {
		const categ = e.target.value

		const newInquiry = produce(inquiry, draft => {
			draft.categ = categ
		})

		reduxDispatch(inquiryActions.INSERT_INQUIRY(newInquiry))
	}

	const onMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const message = e.target.value

		const newInquiry = produce(inquiry, draft => {
			draft.message = message
		})

		reduxDispatch(inquiryActions.INSERT_INQUIRY(newInquiry))
	}

	const onSubmitClick = () => {
		reduxDispatch(inquiryActions.TRIGGER_SEND_INQUIRY(true))
	}

	return (
		<View css={style}>
			<View className="outerView">
				<View className="contentOuterView">
					<Text category="h1">CONTACT</Text>

					<View className="contentView">
						<View className="countactUsArticleView">
							<Text category="h4">
								We’re always here to help and would love to hear from you!
							</Text>
							<Text category="h4">
								Whether you have questions about our products, need support,
							</Text>
							<Text category="h4">
								or just want to share your thoughts, feel free to reach out.
							</Text>
							<Text category="h4">
								Let’s connect and make your experience even better!
							</Text>
							<Text category="h4">We welcome your ideas and feedback!</Text>

							<Text category="h4">
								You can also send a DM to our Instagram @judahs_social for a faster
								response.
							</Text>
							<Text>https://www.instagram.com/judahs_social</Text>

							<View className="belowArticleView">
								<Text category="h4" className="belowArticleTitle">
									- Inquiry Topics -
								</Text>
								<ul>
									<li>Product Inquiries</li>
									<p>Ask us about product details, features, or recommendations.</p>

									<li>Order Support</li>
									<p>Need help with an order or delivery? We’re here for you.</p>

									<li>Feedback & Suggestions</li>
									<p>Share your thoughts or ideas—we’re always looking to improve.</p>

									<li>Collaboration Opportunities</li>
									<p>
										Interested in working with us? We welcome inquiries from YouTubers,
										social media influencers, and other creators. Let’s create something
										amazing together!
									</p>

									<li>Other Questions</li>
									<p>
										If it doesn’t fit the categories above, don’t worry. Just reach out,
										and we’ll do our best to help!
									</p>
								</ul>
							</View>
						</View>

						<View className="inquiryFormView">
							<View className="inputView">
								<InputLabel error={errorMessages.name !== ''}>Name *</InputLabel>
								<TextField
									type="text"
									onChange={onNameChange}
									value={inquiry.name}
									error={errorMessages.name !== ''}
								/>
								{errorMessages.name !== '' && (
									<ErrorText className="errorMsg">{errorMessages.name}</ErrorText>
								)}
							</View>

							<View className="inputView">
								<InputLabel error={errorMessages.email !== ''}>Email *</InputLabel>
								<TextField
									type="text"
									onChange={onEmailChange}
									value={inquiry.email}
									error={errorMessages.email !== ''}
								/>
								{errorMessages.email !== '' && (
									<ErrorText className="errorMsg">{errorMessages.email}</ErrorText>
								)}
							</View>

							<View className="inputView">
								<InputLabel error={errorMessages.categ !== ''}>Category *</InputLabel>
								<Select
									className="categSelect"
									name="categ"
									value={inquiry.categ}
									size="small"
									error={errorMessages.categ !== ''}
									style={{ minHeight: '3.5rem', textAlign: 'left' }}
									onChange={onCategChange}
								>
									<MenuItem value="productInquiries">Product Inquiries</MenuItem>
									<MenuItem value="orderSupport">Order Support</MenuItem>
									<MenuItem value="feedbackSuggestions">Feedback & Suggestions</MenuItem>
									<MenuItem value="collaborationOpportunities">
										Collaboration Opportunities
									</MenuItem>
									<MenuItem value="otherQuestions">Other Questions</MenuItem>
								</Select>
							</View>

							<View className="inputView">
								<InputLabel error={errorMessages.message !== ''}>Message *</InputLabel>
								<Textarea
									className="inputTextarea"
									minRows={10}
									value={inquiry.message}
									onChange={onMessageChange}
									error={errorMessages.message !== ''}
								/>

								{errorMessages.message !== '' && (
									<ErrorText className="errorMsg">{errorMessages.message}</ErrorText>
								)}
							</View>

							<View className="submitView">
								<Pressable className="submitButton" onClick={onSubmitClick}>
									<View>
										<Text category="h2">Submit</Text>
									</View>
								</Pressable>

								<Text>This is a reply email from cs@judahs.io</Text>
								<Text>
									If you do not receive the email, please check your spam or junk folder.
								</Text>
							</View>
						</View>
					</View>
				</View>
			</View>

			<Footer />

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showModal}
				onCloseModal={() => {
					reduxDispatch(inquiryActions.SHOW_MODAL(false))

					if (modalInfo.target === 'submitSuccess') {
						reduxDispatch(
							inquiryActions.INSERT_INQUIRY({
								name: '',
								email: '',
								categ: '',
								message: '',
							}),
						)
					}
				}}
				showSubmit={false}
				closeStatus="inherit"
				closeTitle={'Close'}
				submitTitle="Get Code"
				submitIconColor={deepPurple['A400']}
				isTitleDanger={['submitError'].includes(modalInfo.target)}
			>
				{modalInfo.target === 'submitSuccess' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">Your inquiry has been successfully submitted.</Text>
						<Text category="p2">Thank you for your valuable feedback.</Text>
						<Text category="p2">
							We will respond to you at the earliest possible time.
						</Text>
					</View>
				) : modalInfo.target === 'submitError' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">
							We appreciate your effort to share your valuable feedback,
						</Text>
						<Text category="p2">
							but it could not be submitted due to a server communication issue.
						</Text>
						<Text category="p2">
							We will restore the functionality as soon as possible.
						</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default ContactPage

const style = css({
	position: 'relative',
	width: '100%',

	'.outerView': {
		display: 'flex',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: '100%',
			padding: '2rem',

			'.contentView': {
				width: '100%',
				maxWidth: '60rem',
				margin: 'auto',
				borderTop: '1px solid',
				borderColor: grey[500],

				'.countactUsArticleView': {
					textAlign: 'left',

					'.belowArticleView': {
						'.belowArticleTitle': {
							textAlign: 'center',
						},
						marginTop: '3rem',

						li: {
							marginBottom: '-0.3rem',
							fontSize: '20px',
							fontWeight: '700',
						},
						p: {
							marginBottom: '2rem',
						},
					},
				},

				'.inquiryFormView': {
					'.inputView': {
						width: '100%',
						marginBottom: '1rem',
						textAlign: 'left',

						'.categSelect': {
							width: '100%',
						},
					},
				},

				'.submitView': {
					paddingTop: '2rem',
					textAlign: 'right',

					'.submitButton': {
						paddingLeft: '4rem',
						paddingRight: '4rem',
						background: grey[900],
						color: 'white',
						borderRadius: '5px',
					},
				},
			},
		},
	},
})
